/* eslint-disable react/require-default-props */
import React, { useState } from "react";
import { DIAL_HINT, normalizeDialable } from "../../data/phoneNumbers";
import { SoftphoneStatus } from "../../hooks/useSoftphone";

export type SoftphoneProps = {
  status: SoftphoneStatus;
  identity: string;
  /**
   * Whether the rep has a Twilio number to call out from. Required rather than
   * optional: false hides the dial box, because a rep with no assignment can
   * still receive calls but every call they place is refused by the backend --
   * and a call site quietly forgetting to pass it is the bug this exists to
   * stop.
   */
  canPlaceCalls: boolean;
  incomingFrom: string;
  error: string;
  muted: boolean;
  /** Whether the panel supplies its own external spacing. */
  hasBottomMargin: boolean;
  // eslint-disable-next-line no-unused-vars
  startCall: (phoneNumber: string) => void;
  acceptCall: () => void;
  rejectCall: () => void;
  hangUp: () => void;
  toggleMute: () => void;
  /**
   * "dark" (the default) is the always-dark calls page. "app" follows the
   * light/dark theme of an AppLayout page, matching the home page's cards.
   */
  tone?: "dark" | "app";
};

const STATUS_LABELS: Record<SoftphoneStatus, string> = {
  connecting: "Connecting…",
  ready: "Ready",
  dialing: "Dialing…",
  ringing: "Incoming call",
  "on-call": "On a call",
  unavailable: "Calling unavailable",
};

const TONES = {
  dark: {
    panel:
      "bg-gray-800 bg-opacity-50 backdrop-blur-lg rounded-xl border border-gray-700 p-4",
    title: "text-gray-100",
    muted: "text-gray-400",
    caller: "text-gray-200",
    secondaryButton: "border border-gray-600 text-gray-100 hover:bg-gray-700",
    input: "bg-gray-900 border border-gray-700 text-gray-100",
    callButton: "bg-indigo-600 hover:bg-indigo-500",
    hint: "text-amber-300",
    error: "text-red-400",
  },
  app: {
    // app-* classes live in tailwind.css: Bootstrap's !important utilities
    // would beat bg-white/border/shadow-sm dark: variants on legacy pages.
    panel: "app-card p-4",
    title: "text-gray-900 dark:text-gray-100",
    muted: "text-gray-500 dark:text-gray-400",
    caller: "text-gray-700 dark:text-gray-200",
    secondaryButton: "app-control",
    input: "app-field",
    callButton:
      "bg-blue-700 hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-500",
    hint: "text-amber-700 dark:text-amber-300",
    error: "text-red-600 dark:text-red-400",
  },
};

/**
 * Presentational shell for the browser softphone. All the Device wiring lives
 * in useSoftphone; this only renders its state.
 */
export default function Softphone({
  status,
  identity,
  canPlaceCalls,
  incomingFrom,
  error,
  muted,
  hasBottomMargin,
  startCall,
  acceptCall,
  rejectCall,
  hangUp,
  toggleMute,
  tone = "dark",
}: SoftphoneProps) {
  const colors = TONES[tone];
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [dialHint, setDialHint] = useState<string>("");

  const canDial =
    status === "ready" && canPlaceCalls && phoneNumber.trim() !== "";

  const submitDial = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Twilio would accept the TwiML for an undialable number and the backend
    // would answer it with "Sorry, that number cannot be dialed" -- the rep
    // hears nothing and the field is already blank. Refuse it here instead.
    const dialable = normalizeDialable(phoneNumber);

    if (dialable === null) {
      setDialHint(DIAL_HINT);
      return;
    }

    setDialHint("");
    startCall(dialable);
    setPhoneNumber("");
  };

  return (
    <section
      className={`${colors.panel}${hasBottomMargin ? " mb-6" : ""}`}
      aria-label="Softphone"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className={`text-sm font-medium ${colors.title}`}>
            {status === "ready" && !canPlaceCalls
              ? "Incoming calls only"
              : STATUS_LABELS[status]}
          </p>
          {identity ? (
            <p className={`text-xs ${colors.muted}`}>Signed in as {identity}</p>
          ) : null}
        </div>

        {status === "ringing" ? (
          <div className="flex items-center gap-2">
            <span className={`text-sm ${colors.caller}`}>
              {incomingFrom || "Unknown caller"}
            </span>
            <button
              type="button"
              onClick={acceptCall}
              className="px-3 py-1.5 rounded-md bg-emerald-600 text-sm text-white hover:bg-emerald-500"
            >
              Answer
            </button>
            <button
              type="button"
              onClick={rejectCall}
              className="px-3 py-1.5 rounded-md bg-red-600 text-sm text-white hover:bg-red-500"
            >
              Decline
            </button>
          </div>
        ) : null}

        {status === "on-call" || status === "dialing" ? (
          <div className="flex items-center gap-2">
            {status === "on-call" ? (
              <button
                type="button"
                onClick={toggleMute}
                className={`px-3 py-1.5 rounded-md text-sm ${colors.secondaryButton}`}
              >
                {muted ? "Unmute" : "Mute"}
              </button>
            ) : null}
            <button
              type="button"
              onClick={hangUp}
              className="px-3 py-1.5 rounded-md bg-red-600 text-sm text-white hover:bg-red-500"
            >
              Hang up
            </button>
          </div>
        ) : null}

        {status === "ready" && canPlaceCalls ? (
          <form className="flex items-center gap-2" onSubmit={submitDial}>
            <label className="sr-only" htmlFor="softphone-number">
              Phone number
            </label>
            <input
              id="softphone-number"
              type="tel"
              value={phoneNumber}
              placeholder="(256) 960-8891"
              aria-invalid={dialHint !== ""}
              onChange={(event) => {
                setPhoneNumber(event.target.value);
                setDialHint("");
              }}
              className={`px-3 py-1.5 rounded-md text-sm ${colors.input}`}
            />
            <button
              type="submit"
              disabled={!canDial}
              className={`px-3 py-1.5 rounded-md text-sm text-white disabled:opacity-40 ${colors.callButton}`}
            >
              Call
            </button>
          </form>
        ) : null}
      </div>

      {dialHint ? (
        <p className={`mt-3 text-xs ${colors.hint}`} role="alert">
          {dialHint}
        </p>
      ) : null}
      {error ? <p className={`mt-3 text-xs ${colors.error}`}>{error}</p> : null}
    </section>
  );
}
